<template>
	<div>
		<q-table
			ref="table"
			:data="users_invited"
			:columns="columns_users_invited"
			row-key="id"
			:pagination.sync="server_pagination"
			@request="requestGuests"
			:loading="loading"
			selection="multiple"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
			:selected.sync="users_invited_selected"
			class="q-mt-md"
		>
			<template slot="top">
				<div class="col" />
				<q-btn
					color="tertiary"
					class="q-mr-sm"
					icon="fas fa-file-download"
					:label="$t('download_guest_list')"
					@click="downloadGuestList"
					:disable="users_invited.length < 1 || disableGuests"
				/>
				<q-btn
					color="negative"
					icon="fas fa-ban"
					:label="$t('remove')"
					@click="removeGuests"
					class="q-mr-sm"
					v-if="users_invited_selected.length > 0"
					:disable="disableGuests"
				/>
				<q-btn
					color="secondary"
					icon="fas fa-plus"
					:label="$t('add_guests')"
					@click="showAddGuests"
					:disable="disableGuests"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th auto-width>
					<q-checkbox
						v-model="props.selected"
						indeterminate-value="some"
						:disable="disableGuests"
					/>
				</q-th>

				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props">
				<q-td auto-width>
					<q-checkbox color="primary" v-model="props.selected" :disable="disableGuests" />
				</q-td>

				<q-td key="id" :props="props">
					{{ props.row.id }}
				</q-td>

				<q-td key="name" :props="props">
					{{ props.row.name }}
				</q-td>

				<q-td key="email" :props="props">
					{{ props.row.email }}
				</q-td>

				<q-td key="document" :props="props">
					{{ props.row.document }}
				</q-td>

				<q-td key="branches" :props="props">
					<q-btn
						v-if="props.row.branches.length > 1"
						color="primary"
						text-color="white"
						:label="getQuantityBranches(props.row.branches)"
						no-ripple
					>
						<q-tooltip>
							<p v-if="props.row.branches.length > 1" class="tooltip q-mb-sm">
								{{ $t('branches') }}:
								<span v-for="(branch, index) in props.row.branches" :key="index">
									{{ branch }}
									<span v-if="props.row.branches.length - 1 > index">, </span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
					<span v-if="props.row.branches.length < 2">{{
						String(props.row.branches)
					}}</span>
				</q-td>

				<q-td key="areas" :props="props">
					<q-btn
						v-if="props.row.areas.length > 1"
						color="primary"
						text-color="white"
						:label="props.row.areas ? props.row.areas.length : 0"
						no-ripple
					>
						<q-tooltip>
							<p v-if="props.row.areas.length > 1" class="tooltip q-mb-sm">
								{{ $t('areas') }}:
								<span v-for="(area, index) in props.row.areas" :key="index">
									{{ area }}
									<span v-if="props.row.areas.length - 1 > index">, </span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
					<span v-if="props.row.areas.length < 2">{{ String(props.row.areas) }}</span>
				</q-td>

				<q-td key="profile" :props="props">
					{{ translateProfiles(props.row.profile) }}
				</q-td>

				<q-td key="is_present" :props="props">
					<q-checkbox aria-readonly="true" disable v-model="props.row.is_present" />
				</q-td>

				<q-td key="subscribe_date" :props="props">
					{{ fDate(props.row.subscribe_date) }}
				</q-td>

				<q-td auto-width key="actions" :props="props">
					<q-btn
						round
						flat
						icon="fas fa-trash"
						color="negative"
						@click="removeGuest(props)"
						:disable="disableGuests"
					>
						<q-tooltip>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="fas fa-chevron-left"
					@click="$router.push({ name: 'admin_live_classes' })"
				/>
			</div>
		</div>

		<q-modal
			v-model="show_guests"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_guests = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('guests') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<q-table
						:data="guests_list"
						:columns="guests_columns"
						:pagination.sync="new_guests_pagination"
						:filter="filter_guests"
						@request="requestNewGuests"
						:loading="loading"
						selection="multiple"
						:selected.sync="users_invited_selected"
						row-key="id"
						:no-data-label="$t('no_data_avaliable')"
						:no-results-label="$t('no_results_found')"
						:loading-label="$t('loading_data')"
						:rows-per-page-label="$t('items_per_page')"
						:rows-per-page-options="[5, 10, 50, 100]"
						binary-state-sort
					>
						<template slot="top">
							<q-search
								v-model="filter_guests"
								icon="fas fa-search"
								class="q-mr-lg"
								:placeholder="$t('search')"
								:float-label="$t('search')"
								:debounce="600"
							/>

							<q-select
								chips
								multiple
								v-model="area"
								:options="getAllowedAreas"
								:float-label="$t('area')"
								@input="changeAddFilter()"
								class="q-mr-lg"
							/>

							<q-chips-input
								v-model="branches"
								:float-label="$t('branches')"
								:debounce="600"
								@remove="removeBranch"
								clearable
								class="q-mr-lg"
								@clear="clearAutoComplete"
							>
								<q-autocomplete
									@search="fetchBranches"
									@selected="selectedBranch"
									:min-characters="3"
									:max-results="50"
								/>
							</q-chips-input>

							<q-chips-input
								v-model="companies"
								:float-label="$t('company')"
								:debounce="600"
								@remove="removeCompany"
								clearable
								class="q-mr-lg"
								@clear="clearAutoComplete"
							>
								<q-autocomplete
									@search="fetchCompanies"
									@selected="selectedCompany"
									:min-characters="3"
									:max-results="50"
								/>
							</q-chips-input>

							<q-select
								chips
								multiple
								v-model="user_group"
								:options="getAllowedUserGroups"
								:float-label="$t('user_groups')"
								@input="changeAddFilter()"
								class="q-mr-lg"
							/>

							<div class="col" />
						</template>

						<q-tr slot="header" slot-scope="props" :props="props">
							<q-th auto-width>
								<q-checkbox v-model="props.selected" indeterminate-value="some" />
							</q-th>

							<q-th v-for="col in props.cols" :key="col.name" :props="props">
								{{ $t(col.label) }}
							</q-th>
						</q-tr>

						<q-tr slot="body" slot-scope="props" :props="props">
							<q-td auto-width>
								<q-checkbox color="primary" v-model="props.selected" />
							</q-td>

							<q-td key="id" :props="props">
								{{ props.row.id }}
							</q-td>

							<q-td key="name" :props="props">
								{{ props.row.name }}
							</q-td>

							<q-td key="email" :props="props">
								{{ props.row.email }}
							</q-td>

							<q-td key="profile" :props="props">
								{{ translateProfiles(props.row.profile) }}
							</q-td>
						</q-tr>
					</q-table>
					<div class="row gutter-sm  justify-end q-mt-sm">
						<div class="col-xs-12 col-sm-3 ">
							<q-btn
								v-if="getAllowedProfiles.length"
								class="full-width"
								color="secondary"
								:label="$t('register_new_users')"
								icon="fas fa-plus"
								@click="showCreateUsersGuests()"
							/>
						</div>
						<div class="col-xs-12 col-sm-3">
							<q-btn
								class="full-width"
								color="positive"
								icon="far fa-save"
								:label="$t('add_guests')"
								@click="addGuests()"
								:disable="users_invited_selected.length < 1"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-model="show_create_users"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="hideCreateUsers()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('new_users') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-sm">
						<div class="col-xs-12 col-sm-3">
							<q-field
								:error="new_user.email_error"
								:error-label="
									new_user.email_error ? `${$t('email_not_unique')}` : ''
								"
							>
								<q-input
									:float-label="$t('email')"
									v-model="new_user.email"
									:error="$v.new_user.email.$error"
									@input="$v.new_user.email.$touch"
									@blur="emailUnique()"
									:disable="!this.register_fields.email_fields"
								/>
							</q-field>
						</div>

						<div class="col-xs-12 col-sm-3">
							<q-field
								:error="new_user.document_error"
								:error-label="
									new_user.document_error ? $t('document_not_unique') : ''
								"
							>
								<q-input
									:float-label="$t('document')"
									v-model="new_user.document"
									:error="$v.new_user.document.$error"
									@input="$v.new_user.document.$touch"
									@blur="documentUnique()"
								/>
							</q-field>
						</div>

						<div class="col-xs-12 col-sm-2">
							<q-select
								:float-label="$t('profile')"
								v-model="new_user.profile"
								:options="getAllowedProfiles"
								:error="$v.new_user.profile.$error"
								@input="$v.new_user.profile.$touch"
							/>
						</div>

						<div class="col-xs-12 col-sm-3">
							<q-input
								:float-label="$t('full_name')"
								v-model="new_user.full_name"
								:error="$v.new_user.full_name.$error"
								@input="$v.new_user.full_name.$touch"
							/>
						</div>

						<div class="col-xs-12 col-sm-1">
							<q-btn
								class="q-mt-md"
								round
								flat
								color="positive"
								icon="fas fa-plus"
								@click="addNewExternalUser()"
							/>
						</div>
					</div>

					<div class="row gutter-sm" v-for="(user, index) in new_users" :key="index">
						<div class="col-xs-12 col-sm-3">
							<q-input :float-label="$t('email')" v-model="user.email" disable />
						</div>

						<div class="col-xs-12 col-sm-3">
							<q-input
								:float-label="$t('document')"
								v-model="user.document"
								disable
							/>
						</div>

						<div class="col-xs-12 col-sm-2">
							<q-select
								:float-label="$t('profile')"
								:options="getAllowedProfiles"
								v-model="user.profile"
								disable
							/>
						</div>

						<div class="col-xs-12 col-sm-3">
							<q-input
								:float-label="$t('full_name')"
								v-model="user.full_name"
								disable
							/>
						</div>

						<div class="col-xs-12 col-sm-1">
							<q-btn
								class="q-mt-md"
								color="negative"
								round
								flat
								icon="fas fa-trash"
								@click="removeExternalUser(index)"
							/>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('cancel')"
								icon="fas fa-chevron-left"
								@click="hideCreateUsers()"
							/>
						</div>

						<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
							<q-btn
								color="secondary"
								class="full-width"
								:label="$t('save_new_users')"
								icon="far fa-save"
								@click="addExternalUser()"
								:disable="new_users.length === 0 ? true : false"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, email } from 'vuelidate/lib/validators'
import { date } from 'quasar'

export default {
	name: 'GuestsTab',

	props: ['guest_limit', 'allowed_guest_areas', 'allowed_guest_profiles', 'manageLiveClasses'],

	data() {
		return {
			live_class_id: '',
			users_invited: [],
			users_invited_selected: [],
			show_guests: false,
			filter_guests: '',
			guests_list: [],
			columns_users_invited: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'id',
					sortable: false,
				},
				{
					name: 'name',
					required: true,
					label: 'name',
					align: 'left',
					field: 'name',
					sortable: false,
				},
				{
					name: 'email',
					required: true,
					label: 'email',
					align: 'left',
					field: 'email',
					sortable: false,
				},
				{
					name: 'document',
					required: true,
					label: 'document',
					align: 'left',
					field: 'document',
					sortable: false,
				},
				{
					name: 'branches',
					required: true,
					label: 'branches',
					align: 'left',
					field: 'branches',
					sortable: false,
				},
				{
					name: 'areas',
					required: true,
					label: 'areas',
					align: 'left',
					field: 'areas',
					sortable: false,
				},
				{
					name: 'profile',
					required: true,
					label: 'profile',
					align: 'left',
					field: 'profile',
					sortable: false,
				},
				{
					name: 'is_present',
					required: true,
					label: 'present',
					align: 'left',
					field: 'is_present',
					sortable: false,
				},
				{
					name: 'subscribe_date',
					required: true,
					label: 'date',
					align: 'left',
					field: 'subscribe_date',
					sortable: false,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'actions',
					sortable: false,
				},
			],
			guests_columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'id',
					sortable: false,
				},
				{
					name: 'name',
					required: true,
					label: 'name',
					align: 'left',
					field: 'name',
					sortable: false,
				},
				{
					name: 'email',
					required: true,
					label: 'email',
					align: 'left',
					field: 'email',
					sortable: false,
				},
				{
					name: 'profile',
					required: true,
					label: 'profile',
					align: 'left',
					field: 'profile',
					sortable: false,
				},
			],
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 5,
				descending: false,
				sortBy: null,
			},
			new_guests_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 5,
				descending: false,
				sortBy: null,
			},
			loading: false,
			show_create_users: false,
			new_user: {
				email: '',
				email_error: false,
				document: '',
				document_error: false,
				profile: [],
				full_name: '',
			},
			new_users: [],
			file: '',
			company: [],
			branch: [],
			branches: [],
			profile: [],
			area: [],
			stores: [],
			companies: [],
			allowed_guest_branches: [],
			allowed_guest_companies: [],
			user_group: [],
			user_groups: [],
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			register_fields: 'params/register_fields',
			register_store: 'params/register',
			areas: 'registration_lists/country_areas',
			profiles: 'registration_lists/profiles',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			id_user_language_selected: 'common/id_user_language_selected',
			user_companies: 'common/user_companies',
			user_branches: 'common/user_branches',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '90vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		getAllowedAreas() {
			const allowed_areas = []
			this.allowed_guest_areas.forEach((id) => {
				const area = this.areas.find((a) => a.value == id)
				allowed_areas.push(area)
			})
			return allowed_areas
		},

		getAllowedProfiles() {
			const allowed_profiles = []
			this.allowed_guest_profiles.forEach((id) => {
				const profile = this.profiles.find((a) => a.value == id)
				allowed_profiles.push(profile)
			})
			return allowed_profiles
		},

		getAllowedUserGroups() {
			const allowed_user_groups = []
			const userGroupIds = this.user_groups.map((g) => g.id)

			userGroupIds.forEach((id) => {
				const user_group = this.user_groups.find((a) => a.value == id)
				allowed_user_groups.push(user_group)
			})

			return allowed_user_groups
		},

		disableGuests() {
			if (!this.manageLiveClasses) return true
			else return false
		},
	},

	methods: {
		removeBranch({ index, value }) {
			this.branch.splice(index, 1)

			this.requestNewGuests({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: 5,
					descending: false,
					sortBy: null,
				},
			})
		},

		removeCompany({ index, value }) {
			this.company.splice(index, 1)

			this.requestNewGuests({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: 5,
					descending: false,
					sortBy: null,
				},
			})
		},

		clearAutoComplete() {
			this.branches = []
			this.companies = []
			this.company = []
			this.branch = []
			this.requestNewGuests({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: 5,
					descending: false,
					sortBy: null,
				},
			})
		},

		fetchBranches(terms, done) {
			if (!terms.length) {
				return (this.branches = [])
			}

			this.$axios
				.get(`/admin_companies/branches/get_branches/?search=${terms}`, this.getHeader)
				.then((response) => {
					let branches = []

					branches = response.data.map((branches) => {
						return {
							id: branches.id,
							label: `${branches.name} / ${
								branches.document && branches.register
									? `${branches.document} - ${branches.register}`
									: branches.document
									? branches.document
									: branches.register
									? branches.register
									: ''
							}`,
							value: branches.name,
						}
					})
					done(branches)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		selectedBranch(item) {
			this.branch.push(item.id)

			this.requestNewGuests({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: 5,
					descending: false,
					sortBy: null,
				},
			})
		},

		fetchCompanies(terms, done) {
			if (!terms.length) {
				return (this.companies = [])
			}

			this.$axios
				.get(`/admin_companies/companies/get_companies/?search=${terms}`, this.getHeader)
				.then((response) => {
					let companies = []

					companies = response.data.map((companies) => {
						return {
							id: companies.id,
							label: companies.name,
							value: companies.name,
						}
					})
					done(companies)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		selectedCompany(item) {
			this.company.push(item.id)

			this.requestNewGuests({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: 5,
					descending: false,
					sortBy: null,
				},
			})
		},

		async requestGuests({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			const order = pagination.descending ? '-' : ''
			const order_by = pagination.sortBy ? order + pagination.sortBy : ''

			const data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter_guests,
				ordering: order_by,
			}

			await this.$axios
				.get(
					`/admin_live_classes/live_classes/${
						this.live_class_id
					}/guests/?${this.$qs.stringify(data)}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.users_invited = response.data.results.map((result) => {
						let user = {
							id: result.user.id,
							name: result.user.name,
							email: result.user.email,
							profile: result.user.profile,
							document: result.user.document ? result.user.document : '',
							branches: result.user.branches ? result.user.branches : [],
							areas: result.user.areas ? result.user.areas : [],
							subscribe_date: result.subscribe_date ? result.subscribe_date : '',
							is_present: result.is_present,
							selected: false,
						}
						return user
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		requestNewGuests({ pagination }) {
			this.loading = true
			this.new_guests_pagination = pagination

			const order = pagination.descending ? '-' : ''
			const order_by = pagination.sortBy ? order + pagination.sortBy : ''

			const data = {
				page_size: this.new_guests_pagination.rowsPerPage,
				page: this.new_guests_pagination.page,
				search: this.filter_guests,
				profiles: this.profile,
				user_clients__areas__id__in: this.area.join(','),
				user_data__companies__id__in: this.company.length ? this.company.join(',') : [],
				user_data__company_branches__id__in: this.branch.length
					? this.branch.join(',')
					: [],
				store: this.store,
				ordering: order_by,
				group_user_member__group_user__id__in: this.user_group.length
					? this.user_group.join(',')
					: '',
			}

			this.$axios
				.get(
					`/admin_live_classes/live_classes/${this.live_class_id}/guests/not_invited/?` +
						this.$qs.stringify(data, { arrayFormat: 'repeat' }),
					this.getHeader
				)
				.then((response) => {
					this.new_guests_pagination.rowsNumber = response.data.count
					this.guests_list = response.data.results
						? response.data.results.map((e) => {
								return {
									...e,
									branches: e.branches,
									companies: e.companies,
									areas: e.areas,
									selected: false,
									id: e.id,
									profile: e.profile_name,
									name: e.full_name,
								}
						  })
						: []
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		changeAddFilter() {
			this.requestNewGuests({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: 5,
					descending: false,
					sortBy: null,
				},
			})
		},

		showAddGuests() {
			this.users_invited_selected = []
			this.requestNewGuests({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: 5,
					descending: false,
					sortBy: null,
				},
			})
			this.filter_guests = ''
			;(this.company = []),
				(this.branches = []),
				(this.profile = []),
				(this.area = []),
				(this.user_group = []),
				(this.show_guests = true)
		},

		addGuests() {
			this.$q.loading.show()

			const data = {
				users: this.users_invited_selected.map((e) => e.id),
			}

			if (data.users.length > this.guest_limit) {
				this.$q.notify({
					message: this.$t('guest_qty_limit_above_defined'),
					type: 'negative',
					icon: 'fas fa-ban',
				})

				return
			}

			this.$axios
				.post(
					`/admin_live_classes/live_classes/${this.live_class_id}/guests/`,
					data,
					this.getHeader
				)
				.then(() => {
					this.$q.notify({
						message: this.$t('guests_save_success'),
						type: 'positive',
						icon: 'far fa-save',
					})
				})
				.catch((e) => {
					console.log(e)

					this.$q.notify({
						message: this.$t('guests_save_error'),
						type: 'negative',
						icon: 'fas fa-ban',
					})
				})
				.finally(() => {
					this.$q.loading.hide()
					this.show_guests = false
					this.search = ''
					this.clearGuestsSelection()
				})
		},

		removeGuest(props) {
			this.users_invited_selected = [props.row]
			this.removeGuests()
		},

		removeGuests() {
			this.$q.loading.show()

			const data = {
				users: this.users_invited_selected.map((e) => e.id),
			}

			this.$axios
				.post(
					`/admin_live_classes/live_classes/${this.live_class_id}/guests/remove/`,
					data,
					this.getHeader
				)
				.then(() => {
					this.$q.notify({
						message: this.$t('guests_save_success'),
						type: 'positive',
						icon: 'far fa-save',
					})
				})
				.catch((e) => {
					console.log(e)

					this.$q.notify({
						message: this.$t('guests_save_error'),
						type: 'negative',
						icon: 'fas fa-ban',
					})
				})
				.finally(() => {
					this.$q.loading.hide()
					this.show_guests = false
					this.search = ''
					this.clearGuestsSelection()
				})
		},

		clearGuestsSelection() {
			this.company = []
			this.branches = []
			this.profile = []
			this.area = []
			this.stores = []
			this.companies = []
			this.user_group = []
			this.users_invited_selected = []
			this.filter_guests = ''
			this.requestGuests({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.server_pagination.rowsPerPage,
					descending: false,
					sortBy: null,
				},
			})
		},

		showCreateUsersGuests() {
			this.show_create_users = true
		},

		addNewExternalUser() {
			this.$v.new_user.$touch()

			if (!this.$v.new_user.$error) {
				const user = {
					document: this.new_user.document,
					profile: this.new_user.profile,
					full_name: this.new_user.full_name,
				}

				if (!this.register_fields.email_fields && this.new_user.email === '') {
					let url = process.env.SITE_URL.replace(
						/^(?:https?:\/\/)?(?:www\.)?/i,
						''
					).split('/')[0]

					user.email = `${this.new_user.document}@${url}`
				} else {
					user.email = this.new_user.email
				}

				this.new_users.push(user)

				this.new_user = {
					email: '',
					email_error: false,
					document: '',
					document_error: false,
					profile: '',
					full_name: '',
				}

				this.$v.$reset()
			}
		},

		removeExternalUser(index) {
			this.new_users.splice(index, 1)
		},

		addExternalUser() {
			this.$q.loading.show()

			this.new_users = this.new_users.map((user) => {
				return {
					user_data: {
						document: user.document,
						gender: null,
					},
					user_clients: [{ profile: user.profile }],
					full_name: user.full_name,
					email: user.email,
				}
			})

			if (!this.register_fields.email_fields && this.register_store.default_password) {
				this.new_users = this.new_users.map((u) => ({
					...u,
					password_to_change: this.register_store.default_password,
				}))
			}

			const data = {
				users: this.new_users,
			}

			this.$axios
				.post(
					`/admin_live_classes/live_classes/${
						this.live_class_id
					}/guests/add_external_user/`,
					data,
					this.getHeader
				)
				.then(() => {
					this.$q.notify({
						message: this.$t('new_users_save_success'),
						type: 'positive',
						icon: 'far fa-save',
					})
				})
				.catch((e) => {
					console.log(e)

					this.$q.notify({
						message: this.$t('new_users_save_error'),
						type: 'negative',
						icon: 'fas fa-ban',
					})
					this.$v.$reset()
				})
				.finally(() => {
					this.hideCreateUsers()
					this.requestGuests({ pagination: this.server_pagination })
					this.show_guests = false
					this.$q.loading.hide()
				})
		},

		hideCreateUsers() {
			this.show_create_users = false
			this.new_user = {
				email: '',
				email_error: false,
				document: '',
				document_error: false,
				profile: '',
				full_name: '',
			}
			this.new_users = []
		},

		getQuantityBranches(branches) {
			return branches.length > 0 ? branches.length : '0'
		},

		emailUnique() {
			if (this.new_user.email) {
				const fd = new FormData()
				fd.append('email', this.new_user.email)

				this.$axios
					.post(`/register_user/check_user_email_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) {
							this.new_user.email_error = true
						} else this.new_user.email_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.new_user.email_error = false
		},

		documentUnique() {
			if (this.new_user.document) {
				const fd = new FormData()
				fd.append('document', this.new_user.document)

				this.$axios
					.post(`/register_user/check_user_document_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) {
							this.new_user.document_error = true
						} else this.new_user.document_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.new_user.document_error = false
		},

		getOrder(name) {
			if (name == 'cancel') {
				return this.$q.platform.is.mobile ? 'order-last' : 'order-first'
			} else if (name == 'save') {
				return this.$q.platform.is.mobile ? 'order-first' : 'order-last'
			}
		},

		limitExceededFailsafe() {
			this.$q.notify({
				message: this.$t('guest_qty_limit_above_defined'),
				type: 'negative',
				icon: 'fas fa-ban',
			})
			this.show_guests = false
			this.search = ''
			this.$q.loading.hide()
			this.clearGuestsSelection()
		},

		loadUserGroups() {
			const fd = new FormData()
			fd.append('country', this.id_user_client_country_selected)

			this.$axios
				.post(`/register_user/list_user_groups/`, fd, this.getHeader)
				.then((response) => {
					this.user_groups = response.data.map((e) => {
						return {
							...e,
							value: e.id,
							label: `${e.title}`,
						}
					})
				})
				.catch((e) => {
					console.log(e)
				})
		},

		downloadGuestList() {
			const message = this.$t('generating_guest_list_download_message')
			this.$q.loading.show({
				message,
			})

			this.$axios
				.get(
					`/admin_live_classes/live_classes/${
						this.live_class_id
					}/guests/generate_report/`,
					this.getHeader
				)
				.then((response) => {
					this.forceFileDownload(response.data.path_url)
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		forceFileDownload(response) {
			const link = document.createElement('a')
			link.href = response
			document.body.appendChild(link)
			link.click()
		},

		translateProfiles(value) {
			const profile = value.toUpperCase()
			const term = this.profiles.find((e) => e.translations[0].name.toUpperCase() === profile)
			if (term) {
				let translated = term.label.toLowerCase()
				return translated.charAt(0).toUpperCase() + translated.slice(1)
			} else return value
		},

		fDate(d) {
			if (d) {
				let format = ''

				switch (this.id_user_language_selected) {
					case 1:
					case 3:
						format = 'DD/MM/YYYY HH:mm:ss'
						break
					default:
						format = 'MM-DD-YYYY HH:mm:ss'
						break
				}

				return date.formatDate(d, format)
			}
		},
	},

	created() {
		this.live_class_id = this.$route.params.id

		this.requestGuests({
			pagination: this.server_pagination,
		})

		this.loadUserGroups()
	},

	validations() {
		return {
			new_user: {
				email: {
					email,
					requiredIf: requiredIf(() => {
						return (
							this.register_fields.email_fields &&
							this.register_fields.email_fields_mandatory
						)
					}),
					emailUnique() {
						if (!this.new_user.email_error) return true
						else return false
					},
				},
				document: {
					requiredIf: requiredIf(() => {
						return (
							this.register_fields.document && this.register_fields.document_mandatory
						)
					}),
					documentUnique() {
						if (!this.new_user.document_error) return true
						else return false
					},
				},
				profile: { required },
				full_name: { required },
			},
		}
	},
}
</script>

<style></style>
